import Link from 'next/link'

// Module level: defined inside Button it was a new component type every
// render, so React remounted it each time.
const LoadingSpinner = () => (
  <svg
    className="animate-spin -ml-1 mr-2 h-4 w-4"
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
)

export function Button({
  children,
  variant = 'primary',
  size = 'md',
  href,
  type = 'button',
  disabled = false,
  loading = false,
  fullWidth = false,
  className = '',
  ...props
}) {
  // The identity's button (2026-10-31): sentence case, Inter semibold, 8px
  // corners, a 1px ink rule. One solid button per view at most.
  const baseStyles =
    'inline-flex items-center justify-center rounded-lg font-sans font-semibold transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-foreground disabled:cursor-not-allowed disabled:opacity-50'

  const variants = {
    primary:
      'border border-foreground bg-foreground text-background hover:bg-foreground/85',
    secondary:
      'border border-foreground bg-transparent text-foreground hover:bg-foreground hover:text-background',
    outline:
      'border border-foreground bg-transparent text-foreground hover:bg-muted',
    ghost: 'bg-transparent text-foreground hover:bg-muted',
    destructive:
      'border border-destructive bg-destructive text-destructive-foreground hover:bg-destructive/85',
    muted:
      'border border-border bg-muted text-muted-foreground hover:text-foreground',
    left: 'border border-blue-600 bg-blue-600 text-white hover:bg-blue-700',
    right: 'border border-red-600 bg-red-600 text-white hover:bg-red-700',
  }

  const sizes = {
    xs: 'min-h-8 px-3 text-xs',
    sm: 'min-h-10 px-4 text-sm',
    md: 'min-h-11 px-5 text-sm',
    lg: 'min-h-12 px-6 text-base',
    xl: 'min-h-14 px-8 text-base',
  }

  const widthStyles = fullWidth ? 'w-full' : ''
  const disabledStyles = disabled || loading ? 'cursor-not-allowed' : ''

  const combinedClassName = `${baseStyles} ${variants[variant]} ${sizes[size]} ${widthStyles} ${disabledStyles} ${className}`

  // If href is provided, render as Link
  if (href) {
    return (
      <Link href={href} className={combinedClassName} {...props}>
        {loading && <LoadingSpinner />}
        {children}
      </Link>
    )
  }

  // Otherwise render as button
  return (
    <button
      type={type}
      disabled={disabled || loading}
      className={combinedClassName}
      {...props}
    >
      {loading && <LoadingSpinner />}
      {children}
    </button>
  )
}

// Export variants for easy reference
Button.variants = {
  primary: 'primary',
  secondary: 'secondary',
  outline: 'outline',
  ghost: 'ghost',
  destructive: 'destructive',
  muted: 'muted',
  left: 'left',
  right: 'right',
}

Button.sizes = {
  xs: 'xs',
  sm: 'sm',
  md: 'md',
  lg: 'lg',
  xl: 'xl',
}
